import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, KeyRound, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail = formatApiError(data) || `Server returned HTTP ${res.status}. If this keeps happening, contact support.`;
        throw new Error(detail);
      }
      setSubmitted(true);
    } catch (err) {
      // Surface a friendlier message for network failures (fetch throws
      // TypeError on DNS / CORS / offline); otherwise pass the real message
      // through so production failures are diagnosable from the screen.
      if (err instanceof TypeError) {
        setError('Network error — please check your connection and try again.');
      } else {
        setError((err && typeof err.message === 'string' && err.message) || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <button onClick={() => navigate('/login')} data-testid="forgot-back" className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to login
        </button>
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-gray-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#7A0B7A]/10 rounded-lg">
              <KeyRound className="w-5 h-5 text-[#7A0B7A]" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Forgot password</h1>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            Enter the email address you use to sign in to Ox Resin and we&apos;ll email you a reset link.
          </p>

          {submitted ? (
            <div className="space-y-4" data-testid="forgot-success">
              <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900">
                  If an account exists for <strong>{email}</strong>, you&apos;ll receive a reset link in your inbox within a minute. The link expires in 60 minutes.
                </div>
              </div>
              <Button onClick={() => navigate('/login')} variant="outline" className="w-full" data-testid="forgot-return-login">
                Return to login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-2 block">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    data-testid="forgot-email"
                    className="pl-10 h-12 border-2 rounded-xl"
                  />
                </div>
              </div>
              {error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="forgot-error">{error}</div>
              )}
              <Button type="submit" disabled={submitting} data-testid="forgot-submit" className="w-full h-12 bg-[#7A0B7A] hover:bg-[#5e0860] text-white font-bold">
                {submitting ? 'Sending…' : 'Send reset link'}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                Remembered it? <Link to="/login" className="text-[#7A0B7A] font-semibold hover:underline">Back to login</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
